import { FilterOptionsState } from "@mui/material";
import Autocomplete, {
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
  createFilterOptions,
} from "@mui/material/Autocomplete";
import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonTextField from "../commonTextField/CommonTextField";
import AutocompleteChip from "./AutocompleteChip";
import { Option } from "./types";

type Props = {
  options: Option[];
  value: Option[];
  onChange: (
    event: ChangeEvent<{}>,
    newValue: Option[] | null,
    dialogValue: { dialogOpen: boolean; dialogValue: string },
    reason: AutocompleteChangeReason,
    details?: any,
  ) => void;
  autoHighlight?: boolean;
};

const filter = createFilterOptions<Option>();

const CustomAutocompleteMultiFreesolo = ({
  options,
  onChange,
  value = [],
  autoHighlight = false,
}: Props) => {
  const [selectedOptions, setSelectedOptions] = useState<Option[]>(value);

  const { t } = useTranslation();

  useEffect(() => {
    setSelectedOptions(value);
  }, [value]);

  return (
    <div>
      <Autocomplete
        autoHighlight={autoHighlight}
        multiple
        freeSolo
        options={options}
        isOptionEqualToValue={(option, value) => {
          const optionId = typeof option === "string" ? option : option?.id;
          const valueId = typeof value === "string" ? value : value.id;
          return optionId === valueId;
        }}
        getOptionLabel={(option: string | Option) => {
          if (typeof option === "string") {
            return option;
          }
          return option.name;
        }}
        filterOptions={(
          options: unknown[],
          params: FilterOptionsState<Option>,
        ) => {
          const filtered = filter(options as Option[], params);

          if (params.inputValue !== "") {
            filtered.push({
              inputValue: params.inputValue,
              name: `${t("common:add")} "${params.inputValue}"`,
              id: "",
            });
          }

          return filtered;
        }}
        selectOnFocus
        clearOnBlur
        value={selectedOptions}
        onChange={(event, newValue: (string | Option)[], reason) => {
          const newValueLength = newValue.length;
          const lastOption = newValue[newValueLength - 1] as Option;

          if (lastOption?.inputValue) {
            // option is not found, add new
            const lastOptionFiltered = newValue.slice(0, newValueLength - 1);

            // open dialog from parent to add new item
            // dialogValue can be used to prefill some field of the dialog
            onChange(
              event,
              lastOptionFiltered as Option[],
              { dialogOpen: true, dialogValue: lastOption.inputValue },
              reason,
            );
          } else {
            const validOptions = (newValue as (string | Option)[]).filter(
              (item): item is Option => typeof item !== "string",
            );
            setSelectedOptions(validOptions);
            onChange(
              event,
              validOptions,
              { dialogOpen: false, dialogValue: "" },
              reason,
            );
          }
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const typedOption = option as Option;
            return (
              <AutocompleteChip
                {...getTagProps({ index })}
                label={typedOption.name}
                tooltip={typedOption.tooltip}
              />
            );
          })
        }
        renderOption={(props, option) => {
          if (!(option as Option).inputValue) {
            const optionObject = option as Option;
            return (
              <li {...props} key={optionObject.id}>
                {optionObject.name}
              </li>
            );
          }

          if ((option as Option).inputValue) {
            const optionObject = option as Option;
            return (
              <li {...props} key={optionObject.inputValue}>
                {optionObject.name}
              </li>
            );
          }
        }}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <CommonTextField {...params} />
        )}
      />
    </div>
  );
};

export default CustomAutocompleteMultiFreesolo;
